import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as St from './SavedEstimateList.styles';

import useEstimateSaver from '~/core/hooks/useEstimateSaver';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectCurrentEstimateCode,
  selectEstimateHasBeenSaved,
  updateEstimateHasBeenSaved,
} from '~/redux/slices/estimate';
import InformationModal from '~molecules/InformationModal';

const SavedEstimateList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isEstimateDirty } = useEstimateSaver();
  const {
    fetchedEstimate,
    startNewEstimate,
    loadEstimateCode,
    switchLoadedEstimateCode,
    isFetchingEstimate,
  } = useEstimateContext();

  const currentEstimateCode = useAppSelector(selectCurrentEstimateCode);
  const estimateHasBeenSaved = useAppSelector(selectEstimateHasBeenSaved);
  const [showCompletedModal, setShowCompletedModal] =
    useState(estimateHasBeenSaved);

  const menuItems = useMemo(() => {
    if (!fetchedEstimate) {
      return [];
    }

    const original = {
      label:
        isEstimateDirty && fetchedEstimate.code === currentEstimateCode
          ? `${fetchedEstimate.estimateNumber} - Revised, please save`
          : fetchedEstimate.estimateNumber,
      value: fetchedEstimate.code,
      key: fetchedEstimate.code,
    };

    const revisions = (fetchedEstimate.revisions ?? [])
      .filter((rev) => rev.code !== fetchedEstimate.code)
      .map((version) => ({
        label:
          isEstimateDirty && version.code === currentEstimateCode
            ? `${version.estimateNumber} - Revised, please save`
            : version.estimateNumber,
        value: version.code,
        key: version.code,
      }));

    return [original, ...revisions];
  }, [fetchedEstimate, isEstimateDirty, currentEstimateCode]);

  const currentSelectedValue = useMemo(
    () => loadEstimateCode || currentEstimateCode,
    [loadEstimateCode, currentEstimateCode]
  );

  const fetchedEstimateAddress = useMemo(() => {
    if (!fetchedEstimate) {
      return '';
    }

    const {
      customerAddress1,
      customerAddress2,
      customerCity,
      customerState,
      customerZip,
    } = fetchedEstimate;

    return `${customerAddress1 || customerAddress2}, ${customerCity}, ${customerState}, ${customerZip}`;
  }, [fetchedEstimate]);

  const handleCloseModal = () => {
    dispatch(updateEstimateHasBeenSaved(false));
    setShowCompletedModal(false);
  };

  const handleRevisionChange = (code: string) => {
    switchLoadedEstimateCode(code);
  };

  return (
    <>
      {fetchedEstimate && (
        <St.Container>
          <St.Title>{t('ESTIMATE_LIST.TITLE')}</St.Title>
          <St.Row>
            <St.RowTitle>{t('ESTIMATE_LIST.ESTIMATE_NUMBER')}:</St.RowTitle>
            <St.SelectList
              options={menuItems}
              value={currentSelectedValue}
              style={{ minWidth: 150 }}
              onChange={handleRevisionChange}
              loading={isFetchingEstimate}
            />
            <St.SaveEstimateButton onClick={startNewEstimate}>
              {t('ESTIMATE_LIST.CREATE')}
            </St.SaveEstimateButton>
          </St.Row>
          <St.Row>
            <St.RowTitle>{t('ESTIMATE_LIST.CUSTOMER_NAME')}:</St.RowTitle>{' '}
            {fetchedEstimate.customerName}
          </St.Row>
          <St.Row>
            <St.RowTitle>{t('ESTIMATE_LIST.EMAIL')}:</St.RowTitle>{' '}
            {fetchedEstimate.customerEmail}
          </St.Row>
          <St.Row>
            <St.RowTitle>{t('ESTIMATE_LIST.ADDRESS')}:</St.RowTitle>{' '}
            {fetchedEstimateAddress}
          </St.Row>
          <St.ImageContainer>
            <img
              src="/public/assets/images/EstimateListBG.jpg"
              alt="placeholder"
              width="640"
            />
          </St.ImageContainer>
        </St.Container>
      )}
      <InformationModal
        title={t('ESTIMATE_LIST.MODAL_TITLE')}
        content={t('ESTIMATE_LIST.MODAL_DESCRIPTION')}
        isVisible={showCompletedModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default SavedEstimateList;
